// @ts-strict-ignore
import { DashboardCard } from "@dashboard/components/Card";
import { IconButton } from "@dashboard/components/IconButton";
import ImageUpload from "@dashboard/components/ImageUpload";
import MediaTile from "@dashboard/components/MediaTile";
import { ProductMediaFragment, ProductMediaType } from "@dashboard/graphql";
import ProductMediaVariantsDialog, {
  MediaVariant,
} from "@dashboard/products/components/ProductMediaVariantsDialog";
import { ReorderAction } from "@dashboard/types";
import createMultiFileUploadHandler from "@dashboard/utils/handlers/multiFileUploadHandler";
import {
  Box,
  Button,
  Dropdown,
  EditIcon,
  List,
  Skeleton,
  sprinkles,
  Text,
  Tooltip,
} from "@saleor/macaw-ui-next";
import * as React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { SortableContainer, SortableElement } from "react-sortable-hoc";

import { messages } from "./messages";
import { bySortOrder, mediaFilename, storefrontGallery } from "./utils";

interface ProductMediaVariant extends MediaVariant {
  media: Array<{ id: string }>;
}

interface AssignmentBadgeProps {
  order: number;
  assignedVariants: MediaVariant[];
  onEdit?: () => void;
}

const AssignmentBadge = ({ order, assignedVariants, onEdit }: AssignmentBadgeProps) => {
  const intl = useIntl();
  const isShared = assignedVariants.length === 0;
  const variantLabel = (variant: MediaVariant) => variant.name || variant.sku || variant.id;
  const label = isShared
    ? intl.formatMessage(messages.shared)
    : assignedVariants.length === 1
      ? variantLabel(assignedVariants[0])
      : intl.formatMessage(messages.variantCount, { count: assignedVariants.length });
  const tooltipText = isShared
    ? intl.formatMessage(messages.sharedTooltip)
    : intl.formatMessage(messages.variantTooltip, {
        variants: assignedVariants.map(variantLabel).join(", "),
      });

  return (
    <Box display="flex" alignItems="center" gap={1} __width={148} justifyContent="space-between">
      <Box display="flex" alignItems="center" gap={1} overflow="hidden" minWidth={0}>
        <Box as="span" paddingX={1} borderRadius={2} backgroundColor="default2" flexShrink="0">
          <Text size={1} color="default2">
            #{order}
          </Text>
        </Box>
        <Tooltip>
          <Tooltip.Trigger>
            <Box
              as="span"
              paddingX={1}
              borderRadius={2}
              borderWidth={1}
              borderStyle="solid"
              borderColor={isShared ? "info1" : "warning1"}
              overflow="hidden"
              minWidth={0}
            >
              <Text size={1} color={isShared ? "info1" : "warning1"} whiteSpace="nowrap">
                {label}
              </Text>
            </Box>
          </Tooltip.Trigger>
          <Tooltip.Content>
            <Tooltip.Arrow />
            {tooltipText}
          </Tooltip.Content>
        </Tooltip>
      </Box>
      {onEdit && (
        <Tooltip>
          <Tooltip.Trigger>
            <IconButton
              variant="secondary"
              size="small"
              hoverOutline={false}
              onClick={onEdit}
              data-test-id="edit-media-assignments"
              aria-label={intl.formatMessage(messages.editAssignments)}
            >
              <EditIcon size="small" />
            </IconButton>
          </Tooltip.Trigger>
          <Tooltip.Content>
            <Tooltip.Arrow />
            {intl.formatMessage(messages.editAssignments)}
          </Tooltip.Content>
        </Tooltip>
      )}
    </Box>
  );
};

interface MediaCardProps {
  media: ProductMediaFragment;
  order: number;
  assignedVariants: MediaVariant[];
  /** Not shown in the storefront for the previewed variant. */
  dimmed?: boolean;
  editHref?: string;
  onDelete?: () => void;
  onEditAssignments?: () => void;
}

const MediaCard = ({
  media,
  order,
  assignedVariants,
  dimmed,
  editHref,
  onDelete,
  onEditAssignments,
}: MediaCardProps) => {
  const filename = mediaFilename(media);

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={1}
      __width={148}
      __opacity={dimmed ? 0.35 : 1}
      data-test-id="media-card"
    >
      <MediaTile media={media} editHref={editHref} onDelete={onDelete} />
      <AssignmentBadge
        order={order}
        assignedVariants={assignedVariants}
        onEdit={onEditAssignments}
      />
      <Text
        size={1}
        color="default2"
        title={filename}
        overflow="hidden"
        textOverflow="ellipsis"
        whiteSpace="nowrap"
        data-test-id="media-filename"
      >
        {filename}
      </Text>
    </Box>
  );
};

interface SortableMediaProps extends MediaCardProps {}

const SortableMedia = SortableElement<SortableMediaProps>((props: SortableMediaProps) => (
  <MediaCard {...props} />
));

interface MediaListContainerProps {
  className: string;
  media: ProductMediaFragment[];
  preview: ProductMediaFragment[];
  getAssignedVariants: (id: string) => MediaVariant[];
  getOrder: (id: string) => number;
  isDimmed: (id: string) => boolean;
  onDelete: (id: string) => () => void;
  getEditHref: (id: string) => string;
  onEditAssignments?: (id: string) => void;
}

const MediaListContainer = SortableContainer<MediaListContainerProps>(
  ({
    media,
    preview,
    getAssignedVariants,
    getOrder,
    isDimmed,
    onDelete,
    getEditHref,
    onEditAssignments,
    ...props
  }) => (
    <div {...props}>
      {media.map((mediaObj, index) => (
        <SortableMedia
          key={`item-${index}`}
          index={index}
          media={mediaObj}
          order={getOrder(mediaObj.id)}
          assignedVariants={getAssignedVariants(mediaObj.id)}
          dimmed={isDimmed(mediaObj.id)}
          editHref={getEditHref(mediaObj.id)}
          onDelete={onDelete(mediaObj.id)}
          onEditAssignments={onEditAssignments ? () => onEditAssignments(mediaObj.id) : undefined}
        />
      ))}
      {preview.sort(bySortOrder).map((mediaObj, index) => (
        <MediaTile loading={true} media={mediaObj} key={index} />
      ))}
    </div>
  ),
);

interface ProductMediaProps {
  media: ProductMediaFragment[];
  variants?: ProductMediaVariant[];
  loading?: boolean;
  getImageEditUrl: (id: string) => string;
  onImageDelete: (id: string) => () => void;
  onImageReorder?: ReorderAction;
  onImageUpload: (file: File) => any;
  openMediaUrlModal: () => any;
  onMediaVariantsChange?: (mediaId: string, variantIds: string[]) => void;
}

/** @deprecated This component should use @dnd-kit instead of react-sortable-hoc */
const ProductMedia = (props: ProductMediaProps) => {
  const {
    media,
    variants = [],
    getImageEditUrl,
    onImageDelete,
    onImageReorder,
    onImageUpload,
    openMediaUrlModal,
    onMediaVariantsChange,
  } = props;
  const intl = useIntl();
  const imagesUpload = React.useRef<HTMLInputElement>(null);
  const anchor = React.useRef<HTMLButtonElement>();
  const [imagesToUpload, setImagesToUpload] = React.useState<ProductMediaFragment[]>([]);
  const [previewVariantId, setPreviewVariantId] = React.useState<string | null>(null);
  const [editedMediaId, setEditedMediaId] = React.useState<string | null>(null);

  const orderedMedia = React.useMemo(
    // Stable: ties keep the API order (sort_order, pk) — the order the storefront
    // shows and the reorder handler indexes into.
    () => (media ? [...media].sort(bySortOrder) : media),
    [media],
  );

  // mediaId -> variants that display it
  const assignmentsByMedia = React.useMemo(() => {
    const map = new Map<string, MediaVariant[]>();

    variants.forEach(variant => {
      (variant.media ?? []).forEach(({ id }) => {
        map.set(id, [...(map.get(id) ?? []), variant]);
      });
    });

    return map;
  }, [variants]);

  const getAssignedVariants = (id: string) => assignmentsByMedia.get(id) ?? [];
  const getOrder = (id: string) =>
    (orderedMedia ?? []).findIndex(mediaObj => mediaObj.id === id) + 1;

  // Storefront preview: what the product page gallery shows with no variant
  // selected, or with the previewed variant selected (shared + its own).
  const previewVariant = previewVariantId
    ? variants.find(variant => variant.id === previewVariantId)
    : null;
  const previewMedia = storefrontGallery(orderedMedia ?? [], variants, previewVariant?.id ?? null);
  const previewIds = new Set(previewMedia.map(({ id }) => id));
  const isDimmed = (id: string) => !!previewVariant && !previewIds.has(id);
  const previewOwnCount = previewVariant
    ? previewMedia.filter(({ id }) => getAssignedVariants(id).length > 0).length
    : 0;
  const variantLabel = (variant: MediaVariant) => variant.name || variant.sku || variant.id;
  const showPreview = variants.length > 0 && media?.length > 0;

  const editedMedia = editedMediaId
    ? (media ?? []).find(mediaObj => mediaObj.id === editedMediaId)
    : null;

  const handleImageUpload = createMultiFileUploadHandler(onImageUpload, {
    onAfterUpload: () => setImagesToUpload(prevImagesToUpload => prevImagesToUpload.slice(1)),
    onStart: files => {
      Array.from(files).forEach((file, fileIndex) => {
        const reader = new FileReader();

        reader.onload = event => {
          setImagesToUpload(prevImagesToUpload => [
            ...prevImagesToUpload,
            {
              __typename: "ProductMedia",
              alt: "",
              id: "",
              sortOrder: fileIndex,
              type: ProductMediaType.IMAGE,
              url: event.target.result as string,
              originalUrl: event.target.result as string,
              fileName: file.name,
              oembedData: null,
            },
          ]);
        };
        reader.readAsDataURL(file);
      });
    },
  });

  return (
    <DashboardCard>
      <DashboardCard.Header>
        <DashboardCard.Title>
          <FormattedMessage {...messages.media} />
        </DashboardCard.Title>
        <DashboardCard.Toolbar>
          <Box display="flex" gap={2} alignItems="center">
            <Dropdown>
              <Dropdown.Trigger>
                <Button
                  variant="secondary"
                  type="button"
                  data-test-id="button-upload-image"
                  ref={anchor}
                >
                  {intl.formatMessage(messages.upload)}
                </Button>
              </Dropdown.Trigger>
              <Dropdown.Content align="end">
                <List
                  padding={2}
                  borderRadius={4}
                  boxShadow="defaultOverlay"
                  backgroundColor="default1"
                >
                  <Dropdown.Item>
                    <List.Item
                      borderRadius={4}
                      paddingX={1.5}
                      paddingY={2}
                      onClick={() => imagesUpload.current.click()}
                      data-test-id="upload-images"
                    >
                      <Text>{intl.formatMessage(messages.uploadImages)}</Text>
                    </List.Item>
                  </Dropdown.Item>
                  <Dropdown.Item>
                    <List.Item
                      borderRadius={4}
                      paddingX={1.5}
                      paddingY={2}
                      onClick={openMediaUrlModal}
                      data-test-id="upload-media-url"
                    >
                      <Text>{intl.formatMessage(messages.uploadUrl)}</Text>
                    </List.Item>
                  </Dropdown.Item>
                </List>
              </Dropdown.Content>
            </Dropdown>
          </Box>
        </DashboardCard.Toolbar>
      </DashboardCard.Header>
      <DashboardCard.Content>
        <Box>
          <Box
            as="input"
            display="none"
            id="fileUpload"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              handleImageUpload(event.target.files)
            }
            multiple
            type="file"
            ref={imagesUpload}
            accept="image/*"
          />
        </Box>
        {variants.length > 0 && (
          <Box marginBottom={4}>
            <Text size={2} color="default2">
              <FormattedMessage {...messages.legend} />
            </Text>
          </Box>
        )}
        <Box position="relative">
          {media === undefined ? (
            <Box padding={5}>
              <Skeleton />
            </Box>
          ) : media.length > 0 ? (
            <ImageUpload
              className={sprinkles({
                height: "100%",
                width: "100%",
                position: "absolute",
                top: 0,
                left: 0,
              })}
              isActiveClassName={sprinkles({ zIndex: "1" })}
              disableClick={true}
              hideUploadIcon={true}
              iconContainerActiveClassName={sprinkles({ display: "block" })}
              onImageUpload={handleImageUpload}
            >
              {({ isDragActive }) => (
                <MediaListContainer
                  distance={20}
                  helperClass="dragged"
                  axis="xy"
                  media={orderedMedia}
                  preview={imagesToUpload}
                  onSortEnd={onImageReorder}
                  className={sprinkles({
                    display: "flex",
                    gap: 5,
                    flexWrap: "wrap",
                    opacity: isDragActive ? "0.2" : "1",
                  })}
                  onDelete={onImageDelete}
                  getEditHref={getImageEditUrl}
                  getAssignedVariants={getAssignedVariants}
                  getOrder={getOrder}
                  isDimmed={isDimmed}
                  onEditAssignments={onMediaVariantsChange ? id => setEditedMediaId(id) : undefined}
                />
              )}
            </ImageUpload>
          ) : (
            <ImageUpload onImageUpload={handleImageUpload} />
          )}
        </Box>
        {showPreview && (
          <Box
            marginTop={6}
            padding={4}
            borderRadius={4}
            borderWidth={1}
            borderStyle="solid"
            borderColor="default1"
            backgroundColor="default2"
            data-test-id="storefront-preview"
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              gap={2}
              flexWrap="wrap"
              marginBottom={3}
            >
              <Text size={3} fontWeight="bold">
                <FormattedMessage {...messages.previewTitle} />
              </Text>
              <Dropdown>
                <Dropdown.Trigger>
                  <Button variant="secondary" type="button" data-test-id="preview-variant-select">
                    {previewVariant
                      ? variantLabel(previewVariant)
                      : intl.formatMessage(messages.previewNoVariant)}
                  </Button>
                </Dropdown.Trigger>
                <Dropdown.Content align="end">
                  <List
                    padding={2}
                    borderRadius={4}
                    boxShadow="defaultOverlay"
                    backgroundColor="default1"
                    __maxHeight={320}
                    overflowY="auto"
                  >
                    {[null, ...variants].map(variant => (
                      <Dropdown.Item key={variant?.id ?? "none"}>
                        <List.Item
                          borderRadius={4}
                          paddingX={1.5}
                          paddingY={2}
                          onClick={() => setPreviewVariantId(variant?.id ?? null)}
                        >
                          <Text
                            fontWeight={
                              (variant?.id ?? null) === previewVariantId ? "bold" : "regular"
                            }
                          >
                            {variant
                              ? variantLabel(variant)
                              : intl.formatMessage(messages.previewNoVariant)}
                          </Text>
                        </List.Item>
                      </Dropdown.Item>
                    ))}
                  </List>
                </Dropdown.Content>
              </Dropdown>
            </Box>
            <Text size={2} color="default2" display="block" marginBottom={3}>
              {previewVariant
                ? intl.formatMessage(messages.previewVariantSummary, {
                    shown: previewMedia.length,
                    total: orderedMedia.length,
                    own: previewOwnCount,
                    shared: previewMedia.length - previewOwnCount,
                  })
                : intl.formatMessage(messages.previewAllSummary, { total: orderedMedia.length })}
            </Text>
            <Box display="flex" gap={2} flexWrap="wrap">
              {previewMedia.map((mediaObj, index) => (
                <Box
                  key={mediaObj.id}
                  position="relative"
                  __width={72}
                  __height={72}
                  borderRadius={2}
                  borderWidth={1}
                  borderStyle="solid"
                  borderColor="default1"
                  backgroundColor="default1"
                  overflow="hidden"
                  title={`#${getOrder(mediaObj.id)} · ${mediaFilename(mediaObj)}`}
                  data-test-id="storefront-preview-item"
                >
                  <Box
                    as="img"
                    src={mediaObj.url}
                    alt={mediaObj.alt ?? ""}
                    __width="100%"
                    __height="100%"
                    __objectFit="contain"
                  />
                  <Box
                    position="absolute"
                    __top={2}
                    __left={2}
                    paddingX={1}
                    borderRadius={2}
                    backgroundColor="default1"
                  >
                    <Text size={1}>{index + 1}</Text>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </DashboardCard.Content>

      <ProductMediaVariantsDialog
        open={!!editedMedia}
        media={editedMedia}
        variants={variants}
        assignedVariantIds={editedMedia ? getAssignedVariants(editedMedia.id).map(v => v.id) : []}
        onClose={() => setEditedMediaId(null)}
        onConfirm={variantIds => {
          if (editedMedia && onMediaVariantsChange) {
            onMediaVariantsChange(editedMedia.id, variantIds);
          }

          setEditedMediaId(null);
        }}
      />
    </DashboardCard>
  );
};

ProductMedia.displayName = "ProductMedia";
export default ProductMedia;
